import { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame, useThree, Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls } from '@react-three/drei';
import dat from 'dat.gui';
import { Suspense } from 'react';

// --- GLSL Shaders ---
const vertexShader = `
    // Classic Perlin noise functions
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
    vec3 fade(vec3 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }

    float cnoise(vec3 P) {
        vec3 Pi0 = floor(P); vec3 Pi1 = Pi0 + vec3(1.0);
        Pi0 = mod289(Pi0); Pi1 = mod289(Pi1);
        vec3 Pf0 = fract(P); vec3 Pf1 = Pf0 - vec3(1.0);
        vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
        vec4 iy = vec4(Pi0.yy, Pi1.yy);
        vec4 iz0 = Pi0.zzzz; vec4 iz1 = Pi1.zzzz;
        vec4 ixy = permute(permute(ix) + iy);
        vec4 ixy0 = permute(ixy + iz0); vec4 ixy1 = permute(ixy + iz1);
        vec4 gx0 = ixy0 * (1.0 / 7.0); vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5; gx0 = fract(gx0);
        vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0); vec4 sz0 = step(gz0, vec4(0.0));
        gx0 -= sz0 * (step(0.0, gx0) - 0.5); gy0 -= sz0 * (step(0.0, gy0) - 0.5);
        vec4 gx1 = ixy1 * (1.0 / 7.0); vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5; gx1 = fract(gx1);
        vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1); vec4 sz1 = step(gz1, vec4(0.0));
        gx1 -= sz1 * (step(0.0, gx1) - 0.5); gy1 -= sz1 * (step(0.0, gy1) - 0.5);
        vec3 g000 = vec3(gx0.x,gy0.x,gz0.x); vec3 g100 = vec3(gx0.y,gy0.y,gz0.y); vec3 g010 = vec3(gx0.z,gy0.z,gz0.z); vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
        vec3 g001 = vec3(gx1.x,gy1.x,gz1.x); vec3 g101 = vec3(gx1.y,gy1.y,gz1.y); vec3 g011 = vec3(gx1.z,gy1.z,gz1.z); vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
        vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
        g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
        vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
        g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;
        float n000 = dot(g000, Pf0); float n100 = dot(g100, vec3(Pf1.x, Pf0.yz)); float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z)); float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
        float n001 = dot(g001, vec3(Pf0.xy, Pf1.z)); float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z)); float n011 = dot(g011, vec3(Pf0.x, Pf1.yz)); float n111 = dot(g111, Pf1);
        vec3 fade_xyz = fade(Pf0);
        vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
        vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
        float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
        return 2.2 * n_xyz;
    }

    float pnoise(vec3 P, vec3 rep) {
        vec3 Pi0 = mod(floor(P), rep); vec3 Pi1 = mod(Pi0 + vec3(1.0), rep);
        Pi0 = mod289(Pi0); Pi1 = mod289(Pi1);
        vec3 Pf0 = fract(P); vec3 Pf1 = Pf0 - vec3(1.0);
        vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x); vec4 iy = vec4(Pi0.yy, Pi1.yy);
        vec4 iz0 = Pi0.zzzz; vec4 iz1 = Pi1.zzzz;
        vec4 ixy = permute(permute(ix) + iy); vec4 ixy0 = permute(ixy + iz0); vec4 ixy1 = permute(ixy + iz1);
        vec4 gx0 = ixy0 * (1.0 / 7.0); vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5; gx0 = fract(gx0);
        vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0); vec4 sz0 = step(gz0, vec4(0.0));
        gx0 -= sz0 * (step(0.0, gx0) - 0.5); gy0 -= sz0 * (step(0.0, gy0) - 0.5);
        vec4 gx1 = ixy1 * (1.0 / 7.0); vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5; gx1 = fract(gx1);
        vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1); vec4 sz1 = step(gz1, vec4(0.0));
        gx1 -= sz1 * (step(0.0, gx1) - 0.5); gy1 -= sz1 * (step(0.0, gy1) - 0.5);
        vec3 g000 = vec3(gx0.x,gy0.x,gz0.x); vec3 g100 = vec3(gx0.y,gy0.y,gz0.y); vec3 g010 = vec3(gx0.z,gy0.z,gz0.z); vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
        vec3 g001 = vec3(gx1.x,gy1.x,gz1.x); vec3 g101 = vec3(gx1.y,gy1.y,gz1.y); vec3 g011 = vec3(gx1.z,gy1.z,gz1.z); vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
        vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
        g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
        vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
        g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;
        float n000 = dot(g000, Pf0); float n100 = dot(g100, vec3(Pf1.x, Pf0.yz)); float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z)); float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
        float n001 = dot(g001, vec3(Pf0.xy, Pf1.z)); float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z)); float n011 = dot(g011, vec3(Pf0.x, Pf1.yz)); float n111 = dot(g111, Pf1);
        vec3 fade_xyz = fade(Pf0);
        vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
        vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
        float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
        return 1.5 * n_xyz;
    }

    varying vec2 vUv;
    varying float noise;
    varying float qnoise;
    varying float displacement;
    varying vec3 vPosition;

    uniform float time;
    uniform float turb;
    uniform float u_noise;
    uniform float decay;
    uniform float waves;
    uniform float scale;
    uniform float displ;
    uniform float pointscale;
    uniform float eqcolor;
    uniform float rcolor;
    uniform float gcolor;
    uniform float bcolor;
    uniform bool fragment;
    
    uniform vec3 mousePosition;
    uniform float mouseStrength;
    uniform float mouseRadius;

    float turbulence(vec3 p) {
        float t = -turb;
        for (float f = 1.5; f <= 2.0; f++) {
            float power = pow(2.0, f);
            t += abs(pnoise(vec3(power * p), vec3(10.0, 10.0, 10.0)) / power);
        }
        return t;
    }

    void main() {
        vUv = uv;
        vPosition = position;

        noise = (u_noise * -waves) * turbulence(decay * normal + vec3(time));
        qnoise = (eqcolor * -0.8) * turbulence(decay * normal + vec3(time * 0.8 + 0.1));

        float b = pnoise(0.5 * position + vec3(time), vec3(10.0));

        if (fragment) {
            displacement = noise * scale;
        } else {
            float safe_noise = clamp(noise, -1.99, 0.99);
            displacement = log(abs(safe_noise) + 0.1) + tan(b * displ * 0.1) - scale;
        }

        displacement = clamp(displacement, -6.0, 15.0);
        
        vec3 newPosition = position + normal * displacement;
        
        // Add subtle flowing distortion for an organic feel
        newPosition.x += sin(position.z * 2.0 + time) * 0.01;
        newPosition.y += sin(position.x * 2.0 + time * 0.7) * 0.01;
        newPosition.z += sin(position.y * 2.0 + time * 0.5) * 0.01;
        
        // Calculate distance to mouse position (in world space)
        vec3 worldPosition = (modelMatrix * vec4(newPosition, 1.0)).xyz;
        float distanceToMouse = distance(worldPosition, mousePosition);
        
        // Calculate mouse attraction factor
        float mouseFactor = 1.0 - smoothstep(0.0, mouseRadius, distanceToMouse);
        
        // Direction toward mouse (normalized)
        vec3 directionToMouse = normalize(mousePosition - worldPosition);
        
        // Apply mouse attraction - stronger when closer to mouse
        vec3 mouseAttraction = directionToMouse * mouseFactor * mouseStrength;
        
        // Apply to position (in world space, then back to local)
        vec3 attractedWorldPos = worldPosition + mouseAttraction;
        vec3 attractedLocalPos = (inverse(modelMatrix) * vec4(attractedWorldPos, 1.0)).xyz;
        
        // The final position is either pure noise-based or mouse-influenced
        vec3 finalPosition = mix(newPosition, attractedLocalPos, mouseFactor * 0.5);
        
        vec4 mvPosition = modelViewMatrix * vec4(finalPosition, 1.0);

        gl_Position = projectionMatrix * mvPosition;
        gl_PointSize = pointscale * (55.0 / -mvPosition.z);
    }
`;

const fragmentShader = `
    varying float qnoise;
    varying float noise;
    varying vec3 vPosition;
    
    uniform float time;
    uniform float eqcolor;
    uniform float rcolor;
    uniform float gcolor;
    uniform float bcolor;
    
    // Theme color based on #00CF9D
    vec3 themeColor = vec3(0.0, 0.812, 0.615); // Updated to match #00CF9D accurately
    
    void main() {
        float r, g, b;
        
        // Set red to zero
        r = 0.0;
        
        // Only use green and blue channels
        g = pow(abs(qnoise + gcolor), 3.0);
        b = pow(abs(qnoise + bcolor), 3.0);
        
        // Enforce color constraints
        g = clamp(g, 0.0, 3.5);
        b = clamp(b, 0.0, 5.5);
        
        // Create base color with zero red
        vec3 baseColor = vec3(0.0, g, b);
        
        // Mix with theme color with higher bias toward theme color
        vec3 finalColor = mix(baseColor, themeColor, 0.7 + sin(time * 0.5) * 0.1);
        
        gl_FragColor = vec4(finalColor, 1.0);
    }
`;

// Default configuration
const defaultConfig = {
    scene: {
        speed: 0.26,
    },
    shader: {
        time: 0.07,
        u_noise: 8.9,
        decay: .8,
        turb: 0.1,
        scale: 0.15,
        waves: .95,
        size: 0.072,
        displ: 1.85,
        broken: true,
        invert: false,
        eqcolor: 1.0,
        rcolor: 0.0,   // Set to 0 for no red component
        gcolor: 0.82,  // Set to 0.81 for strong green (matches #00CF9D)
        bcolor: 0.72,  // Set to 0.62 for medium blue (matches #00CF9D)
        mouseStrength: -5.25,
        mouseRadius: 4.25
    },
    showParticles: false,
    showText: false
};

// Custom cursor styles
const CustomCursor = () => {
    const cursorRef = useRef(null);
    const midCursorRef = useRef(null);
    const guideRef = useRef(null);

    useEffect(() => {
        const cursor = cursorRef.current;
        const midCursor = midCursorRef.current;
        const guide = guideRef.current;
        
        if (!cursor || !midCursor || !guide) return;
        
        let x_ = 0;
        let y_ = 0;
        let _x = 0;
        let _y = 0;
        let _xm_ = 0;
        let _ym_ = 0;
        let _x_ = 0;
        let _y_ = 0;
        const a = 0.4; // Follow speed
        const cursorSize = 60;

        const handleMouseMove = (e) => {
            _x = e.clientX;
            _y = e.clientY;
        };

        const handleMouseDown = () => {
            cursor.style.width = `${cursorSize}px`;
            cursor.style.height = `${cursorSize}px`;
            cursor.style.border = '1x solid #00CF9D';
            cursor.style.opacity = '0.5';
        };

        const handleMouseUp = () => {
            cursor.style.width = `${cursorSize / 2.3}px`;
            cursor.style.height = `${cursorSize / 2.3}px`;
            cursor.style.border = '20px solid #444758';
            cursor.style.opacity = '0.1';
        };

        const animateCursor = () => {
            x_ += (_x - x_) * a / 2;
            y_ += (_y - y_) * a / 2;
            _x_ += (_x - _x_) * a;
            _y_ += (_y - _y_) * a;
            _xm_ += (_x - _xm_) * a / 1.5;
            _ym_ += (_y - _ym_) * a / 1.5;
            
            cursor.style.left = `${x_}px`;
            cursor.style.top = `${y_}px`;
            midCursor.style.left = `${_xm_}px`;
            midCursor.style.top = `${_ym_}px`;
            guide.style.left = `${_x_}px`;
            guide.style.top = `${_y_}px`;
            
            requestAnimationFrame(animateCursor);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        
        animateCursor();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <>
            <div 
                ref={cursorRef} 
                style={{
                    position: 'fixed',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    border: '20px solid #444758',
                    opacity: '0.1',
                    pointerEvents: 'none',
                    zIndex: 9999,
                    transform: 'translate(-50%, -50%)',
                    transition: 'width 0.2s, height 0.2s, opacity 0.2s, border 0.2s'
                }}
                className="mouse"
            />
            <div 
                ref={midCursorRef}
                style={{
                    position: 'fixed',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 207, 157, 0.3)',
                    pointerEvents: 'none',
                    zIndex: 9999,
                    transform: 'translate(-50%, -50%)'
                }}
                className="mouse-mid"
            />
            <div 
                ref={guideRef}
                style={{
                    position: 'fixed',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#00CF9D',
                    pointerEvents: 'none',
                    zIndex: 9999,
                    transform: 'translate(-50%, -50%)'
                }}
                className="mouse-guide"
            />
        </>
    );
};

// Particles component
const Particles = ({ visible = false, count = 50, isMobile = false }) => {
    const particlesRef = useRef();
    const [particles, setParticles] = useState([]);
    
    // Calculate particle size based on device
    const particleSize = isMobile ? 0.3 : 0.1; // 3x larger on mobile
    
    useEffect(() => {
        const newParticles = [];
        const amplitude = 20;
        
        for (let i = 0; i < count; i++) {
            newParticles.push({
                position: [
                    Math.random() * amplitude - (amplitude / 2),
                    Math.random() * (amplitude * 2) - amplitude,
                    Math.random() * amplitude - (amplitude / 2)
                ],
                velocity: 0,
                velocityX: Math.random() * 0.001,
                velocityZ: Math.random() * 0.001
            });
        }
        
        setParticles(newParticles);
    }, [count]);
    
    useFrame(({ clock }) => {
        if (!particlesRef.current || !visible) return;
        
        const positions = particlesRef.current.geometry.attributes.position.array;
        
        particles.forEach((particle, i) => {
            // Update particle velocity
            particle.velocity -= 0.001 + Math.random() * 0.0005;
            
            // Update Y position with velocity
            particle.position[1] += particle.velocity;
            
            // Reset particle if it goes below the scene
            if (particle.position[1] <= -20) {
                particle.position[1] = 20;
                particle.velocity = 0;
            }
            
            // Update position in the buffer
            const idx = i * 3;
            positions[idx] = particle.position[0];
            positions[idx + 1] = particle.position[1];
            positions[idx + 2] = particle.position[2];
        });
        
        particlesRef.current.geometry.attributes.position.needsUpdate = true;
    });
    
    if (!visible) return null;
    
    return (
        <points ref={particlesRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={particles.length}
                    array={new Float32Array(particles.length * 3)}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial color="#00CF9D" size={particleSize} transparent opacity={0.6} />
        </points>
    );
};

// Main component for the distorted sphere with GUI controls
export const FluidDistortedSphere = ({ config = defaultConfig, enableControls = true, developmentMode = false, isMobile = false }) => {
    const pointsRef = useRef();
    const groupRef = useRef();
    
    // State for GUI
    const [showGUI, setShowGUI] = useState(developmentMode);
    const [guiRef, _] = useState({ current: null });
    
    // Access Three.js state
    const { camera, size } = useThree();
    
    // Reference for raycaster and mouse position
    const raycaster = useMemo(() => new THREE.Raycaster(), []);
    const mouseWorldPos = useMemo(() => new THREE.Vector3(), []);
    
    // Store normalized mouse position in component state
    const mousePosRef = useRef({ x: 0, y: 0 });
    
    // Set scale factor for mobile - making it much smaller on mobile
    const mobileScale = useMemo(() => {
        return isMobile ? 0.065 : .95; // 30% of original size on mobile
    }, [isMobile]);
    
    // Mobile-specific noise adjustments - enhance distortion for mobile
    const mobileNoiseFactors = useMemo(() => {
        if (isMobile) {
            return {
                u_noise: 0.6,     // Increase base noise (multiplier)
                waves: 4.4,       // Enhance waves effect (multiplier)
                turbulence: 1.4,  // More turbulence (multiplier)
                displacement: 1.4, // More displacement (multiplier)
                speedFactor: .5   // Speed up animation
            };
        }
        return {
            // Apply same enhanced values to desktop to match mobile appearance
            u_noise: 1.2,     // Same as mobile
            waves: 1.2,        // Same as mobile
            turbulence: 1.4,   // Same as mobile
            displacement: 11.4, // Same as mobile
            speedFactor: .35    // Same as mobile
        };
    }, [isMobile]);
    
    // Conditionally disable OrbitControls completely for mobile
    const OrbitControlsComponent = useMemo(() => {
        // Return null component for mobile devices
        if (isMobile) {
            return () => null;
        }
        // Return actual OrbitControls for desktop
        return OrbitControls;
    }, [isMobile]);
    
    // Remove all event listeners on mobile
    useEffect(() => {
        if (isMobile) {
            // If mobile, don't set up any event listeners
            return;
        }
        
        // Rest of the event handling code for desktop only...
        const updatePointerPosition = (clientX, clientY) => {
            mousePosRef.current = {
                x: (clientX / window.innerWidth) * 2 - 1,
                y: -(clientY / window.innerHeight) * 2 + 1
            };
        };
        
        const handleMouseMove = (event) => {
            updatePointerPosition(event.clientX, event.clientY);
        };
        
        window.addEventListener('mousemove', handleMouseMove);
        
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [isMobile]);

    // UseMemo for Geometry and Material with FULL DESKTOP QUALITY for all devices
    const { geometry, material } = useMemo(() => {
        console.log("Creating Geometry and Material with consistent high quality");
        
        // Use high quality geometry for all devices
        const geom = new THREE.IcosahedronGeometry(1, 65);
        
        const uniforms = {
            time: { value: 0.0 },
            turb: { value: config.shader.turb },
            u_noise: { value: config.shader.u_noise },
            decay: { value: config.shader.decay },
            waves: { value: config.shader.waves },
            eqcolor: { value: config.shader.eqcolor },
            rcolor: { value: config.shader.rcolor },
            gcolor: { value: config.shader.gcolor },
            bcolor: { value: config.shader.bcolor },
            pointscale: { value: config.shader.size },
            scale: { value: config.shader.scale },
            displ: { value: config.shader.displ },
            fragment: { value: config.shader.broken },
            mousePosition: { value: new THREE.Vector3(0, 0, 5) }, 
            mouseStrength: { value: config.shader.mouseStrength },
            mouseRadius: { value: config.shader.mouseRadius }
        };

        const mat = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            depthWrite: false,
            transparent: true,
        });

        return { geometry: geom, material: mat };
    }, [config]);

    // Animation loop
    useFrame(({ clock }) => {
        if (pointsRef.current && material) {
            const time = clock.getElapsedTime();
            
            // Update shader uniforms from GUI values with mobile adjustments
            material.uniforms.time.value = time * config.shader.time * mobileNoiseFactors.speedFactor;
            material.uniforms.decay.value = config.shader.decay;
            material.uniforms.u_noise.value = config.shader.u_noise * mobileNoiseFactors.u_noise;
            material.uniforms.waves.value = config.shader.waves * mobileNoiseFactors.waves;
            material.uniforms.displ.value = config.shader.displ * mobileNoiseFactors.displacement;
            material.uniforms.turb.value = config.shader.turb * mobileNoiseFactors.turbulence;
            material.uniforms.scale.value = config.shader.scale;
            material.uniforms.eqcolor.value = config.shader.eqcolor;
            material.uniforms.rcolor.value = config.shader.rcolor;
            material.uniforms.gcolor.value = config.shader.gcolor;
            material.uniforms.bcolor.value = config.shader.bcolor;
            
            // Apply rotation based on mouse position
            if (groupRef.current) {
                // Base auto-rotation - speed up rotation on mobile for more dynamic appearance
                groupRef.current.rotation.y += (config.scene.speed / 100) * mobileNoiseFactors.speedFactor;
                
                // Mouse-driven rotation
                const targetRotationY = mousePosRef.current.x * Math.PI * 0.3;
                const targetRotationX = -mousePosRef.current.y * Math.PI * 0.3;
                
                groupRef.current.rotation.y += (targetRotationY - groupRef.current.rotation.y) * 0.1;
                groupRef.current.rotation.x += (targetRotationX - groupRef.current.rotation.x) * 0.1;
            }
            
            // Update raycaster with mouse position
            raycaster.setFromCamera(
                new THREE.Vector2(mousePosRef.current.x, mousePosRef.current.y),
                camera
            );

            const mouseRay = raycaster.ray;
            mouseWorldPos.copy(mouseRay.origin).add(mouseRay.direction.multiplyScalar(5));
            
            material.uniforms.mousePosition.value.copy(mouseWorldPos);
        }
    });

    // Setup GUI only for development mode
    useEffect(() => {
        if (guiRef.current || !developmentMode) return;
        
        try {
            const gui = new dat.GUI();
            guiRef.current = gui;
            
            const materialFolder = gui.addFolder('Material');
            const colorFolder = gui.addFolder('Colors');
            
            materialFolder.add(config.shader, 'time', 0.1, 1.0).name('Speed');
            materialFolder.add(config.shader, 'decay', 0.1, 5.0).name('Decay');
            materialFolder.add(config.shader, 'waves', 0.0, 5.0).name('Waves');
            materialFolder.add(config.shader, 'displ', 0.0, 5.0).name('Displace');
            materialFolder.add(config.shader, 'u_noise', 0.0, 10.0).name('Noise');
            materialFolder.add(config.shader, 'scale', 0.0, 1.0).name('Scale');
            
            colorFolder.add(config.shader, 'eqcolor', 1.0, 30.0).name('Color Eq');
            colorFolder.add(config.shader, 'rcolor', 0.0, 2.0).name('Red');
            colorFolder.add(config.shader, 'gcolor', 0.0, 2.0).name('Green');
            colorFolder.add(config.shader, 'bcolor', 0.0, 2.0).name('Blue');
            
            materialFolder.open();
            colorFolder.open();
            
            return () => {
                if (guiRef.current) {
                    guiRef.current.destroy();
                    guiRef.current = null;
                }
            };
        } catch (error) {
            console.log('GUI initialization error:', error);
        }
    }, [config, developmentMode]);

    return (
        <>
            <group 
                ref={groupRef} 
                scale={mobileScale} // Apply mobile scaling factor
                position={[0, isMobile ? -0.05 : 0, 0]} // Adjust vertical position on mobile
            >
                <points ref={pointsRef} geometry={geometry} material={material} />
            </group>
            
            <Particles visible={config.showParticles} count={150} isMobile={isMobile} />
            
            {/* Use the conditional component instead of conditional rendering */}
            {enableControls && !isMobile && (
                <OrbitControlsComponent
                    enableDamping
                    dampingFactor={0.05}
                    rotateSpeed={0.9}
                    enableZoom={false}
                    minDistance={1.5}
                    maxDistance={2.5}
                    enablePan={false}
                    target={[0, 0, 0]}
                />
            )}
        </>
    );
};

// Complete component with canvas, UI, and custom cursor
export const FluidNoiseScene = ({ showText = false, enableControls = true, developmentMode = false }) => {
    const isMobile = useMemo(() => {
        if (typeof window !== 'undefined') {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
        }
        return false;
    }, []);

    return (
        <div className="w-full h-full" style={{ position: 'relative' }}>
            {/* Add a transparent overlay div on mobile to block all interactions */}
            {isMobile && (
                <div 
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10, // Place above the canvas
                        pointerEvents: 'auto', // Capture all events
                        touchAction: 'pan-y', // Allow vertical scrolling
                        // Make it completely transparent
                        background: 'transparent',
                    }}
                />
            )}
            
            <Canvas
                camera={{ position: [0, 0, 5], fov: 75 }}
                gl={{
                    antialias: true,
                    alpha: true,
                    powerPreference: 'high-performance',
                    failIfMajorPerformanceCaveat: false
                }}
                dpr={2}
                onCreated={({ gl, events }) => {
                    gl.setPixelRatio(2);
                    gl.setClearColor(new THREE.Color(0x000000), 0);
                    
                    // For mobile, completely disable all events
                    if (isMobile) {
                        // Disable the entire events system from react-three-fiber
                        if (events) {
                            events.enabled = false;
                            events.connected = false;
                        }
                        
                        // Make the canvas non-interactive
                        gl.domElement.style.pointerEvents = 'none';
                        gl.domElement.style.touchAction = 'none';
                    }
                }}
            >
                <Suspense fallback={null}>
                    <FluidDistortedSphere 
                        enableControls={!isMobile && enableControls}
                        developmentMode={developmentMode}
                        isMobile={isMobile}
                    />
                </Suspense>
            </Canvas>
        </div>
    );
};

export default FluidNoiseScene; 